import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';





export interface Tab {
    id: string; // usually the path
    path: string;
    title: string;
    translationKey?: string;
    closable: boolean;
    component: ReactNode;
    icon?: ReactNode;
}

interface TabContextType {
    tabs: Tab[];
    activeTabPath: string;
    addTab: (tab: Tab) => void;
    removeTab: (path: string) => void;
    activateTab: (path: string) => void;
    clearTabs: () => void;
}

const TabContext = createContext<TabContextType | undefined>(undefined);

export const useTabContext = () => {
    const context = useContext(TabContext);
    if (!context) {
        throw new Error('useTabContext must be used within a TabProvider');
    }
    return context;
};

interface TabProviderProps {
    children: ReactNode;
}

export const TabProvider: React.FC<TabProviderProps> = ({ children }) => {
    const [tabs, setTabs] = useState<Tab[]>([]);

    const location = useLocation();
    const navigate = useNavigate();
    const [activeTabPath, setActiveTabPath] = useState(location.pathname);

    // Sync active tab with location
    useEffect(() => {
        setActiveTabPath(location.pathname);
    }, [location.pathname]);

    const addTab = (newTab: Tab) => {
        setTabs(prev => {
            if (prev.some(t => t.path === newTab.path)) {
                return prev;
            }
            return [...prev, newTab];
        });
    };

    const removeTab = (path: string) => {
        setTabs(prev => {
            // Don't remove non-closable tabs
            const tabToRemove = prev.find(t => t.path === path);
            if (tabToRemove && !tabToRemove.closable) return prev;

            const newTabs = prev.filter(t => t.path !== path);

            // If we closed the active tab, navigate to the last available tab
            if (path === activeTabPath && newTabs.length > 0) {
                const lastTab = newTabs[newTabs.length - 1];
                navigate(lastTab.path);
            }

            return newTabs;
        });
    };

    const activateTab = (path: string) => {
        navigate(path);
    };

    const clearTabs = () => {
        setTabs([]);
    };

    return (
        <TabContext.Provider value={{ tabs, activeTabPath, addTab, removeTab, activateTab, clearTabs }}>
            {children}
        </TabContext.Provider>
    );
};
